const state = {
  // Old
  audio: null,

  // New
  audioContext: null,

  /**
   * Holds references to audio nodes, since the JS API has no
   * way of exposing access to nodes connected to the audio context.
   */
  _nodeCache: {
    gain: null,
    silent: null,
  },

  /**
   * Cache of basically raw audio bytes.
   * [key: url]: AudioBuffer
   */
  _bufferCache: {}
};

const actions = {
  playAudioOld({ state }, { url, volume }) {
    if (!url) return;

      try {
        state.audio?.pause();
        state.audio = null;
        state.audio = new Audio(url);
        state.audio.volume = volume || 0.5;
        state.audio.play().catch(() => {});
    } catch (e) {}
  },
  initAudioContext({ state, dispatch }) {
    /**
     * The audio system is sort of like a transformation pipeline.
     * You plug nodes together manually to control the flow of audio.
     * A context is the overarching "controller", and holds the user's
     * default sound output (destination)
     *
     * Connections are set up as:
     * - <Sound effect node> -> Volume Controller (gain) -> AudioContext destination
     * - <Silent sound> -> AudioContext destination
     */

    // Set up the "audio player" itself if it doesn't exist
    if (!state.audioContext) {
      // Webkit one is for very old browsers
      state.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.debug("[Audio] Context initialized");
    }

    // If it does exist, always try to resume it if it is suspended, so we can play audio
    if (state.audioContext.status === 'suspended') {
      state.audioContext.resume();
      console.debug("[Audio] Context resumed");
    }

    /**
     * Set up a volume controller if one does not exist already.
     * The audio context must be available too, as we set it as the output.
     */
    if (!state._nodeCache.gain && state.audioContext) {
      // Create volume controller
      state._nodeCache.gain = new GainNode(state.audioContext, {
        gain: 0.5, // Default volume
      });

      // Connect the volume to the sound output
      state._nodeCache.gain.connect(state.audioContext.destination);

      console.debug("[Audio] Gain node initialized");
    }

    // Set up a silent audio that plays continuously in the background, to keep the audio context alive
    if (!state._nodeCache.silent && state.audioContext) {
      dispatch('initSilentAudio');
    }
  },
  /**
   * Retrieves the audio buffer, either from cache if available, or from a URL
   * @param state
   * @param {string} url
   * @returns {Promise<AudioBuffer>}
   */
  async getAudioBuffer({ state }, { url }) {
    /**
     * Cache is disabled for now, I don't think it is needed, but we'll see.
     * Reason is that it is a static file, so the browser will be caching the
     * resource for up to a year based on current header settings.
     *
     * The cache is fetched from memory if available, or disk if there,
     * so we shouldn't really need the manual memory-only cache.
     */
    // if (url in state._bufferCache) return state._bufferCache[url];

    return fetch(url)
      .then(r => r.arrayBuffer())
      .then(buffer => state.audioContext.decodeAudioData(buffer))
      .then(decoded => {
        // state._bufferCache[url] = decoded;
        return decoded;
      });
  },
  async playAudio({ state, dispatch }, { url, volume }) {
    if (!url) return;

    // Ideally it is always initialized before you even reach this, but check just in case
    if (!state.audioContext) {
      await dispatch('initAudioContext');
    }

    dispatch('getAudioBuffer', { url })
      .then(decoded => {
        /**
         * We must create a new AudioNode from the sound to be played every time,
         * since AudioNodes cannot be re-used after having been played.
         */
        const audioNode = new AudioBufferSourceNode(state.audioContext, {
          buffer: decoded,
        });

        /**
         * Plug the sound node into the volume controller, which is already connected to output/AudioContext.
         * Adjust the volume of the gain controller to the input argument.
         */
        audioNode.connect(state._nodeCache.gain);
        state._nodeCache.gain.gain.value = volume || 0.5;

        // Play the sound
        audioNode.start(0);

        /**
         * Disconnect outputs (gain node) of the audio node when it is done,
         * so that it has no references and can be garbage collected
         */
        audioNode.onended = () => audioNode.disconnect();
      })
      .catch(console.error);
  },
  /**
   * Creates audio from scratch that is completely silent.
   * It plays in the background in order to keep the audio context alive, which prevents
   * us from losing access to play sounds from the browser after periods of inactivity.
   * It is a short 3-second mono channel filled with empty sound (0's) that is played
   * in a continuous loop.
   */
  initSilentAudio({ state }) {
    // Create a buffer to contain sound, 1 channel, 3 seconds based on sample rate
    const buffer = state.audioContext.createBuffer(1, state.audioContext.sampleRate * 3, state.audioContext.sampleRate);

    // Fill the channel with empty sound
    const channelBuffer = buffer.getChannelData(0);
    for (let i=0;i<channelBuffer.length; i++) channelBuffer[i] = 0;

    // Create a source node that will use this buffer and play in repeat
    const source = state.audioContext.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    // Connect the empty sound to the output and then start playing
    source.connect(state.audioContext.destination);
    source.start(0);

    // Keep it around for cleanup
    state._nodeCache.silent = source;

    console.debug("[Audio] Silence initialized");
  },
  /**
   * Stops the entire sound system and cleans it up
   */
  purgeAudioSystem({ state }) {
    if (state._nodeCache.silent) {
      state._nodeCache.silent.stop();
      state._nodeCache.silent.disconnect();
      state._nodeCache.silent = null;
      console.debug("[Audio] Silence purged");
    }

    if (state._nodeCache.gain) {
      state._nodeCache.gain.disconnect();
      state._nodeCache.gain = null;
      console.debug("[Audio] Gain purged");
    }

    if (state.audioContext) {
      state.audioContext.suspend();
      state.audioContext.close();
      state.audioContext = null;
      console.debug("[Audio] Audio context purged");
    }

    for (const key in state._bufferCache) {
      delete state._bufferCache[key];
    }
  }
};

const mutations = {};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};
